import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Upload, FileText, CheckCircle2, XCircle, Loader2, RotateCcw,
  AlertTriangle, ExternalLink, Sparkles, User, Eye, Save,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  uploaded:                 { label: "Uploaded",         class: "bg-zinc-100 text-zinc-700" },
  extracting:               { label: "Extracting",       class: "bg-blue-100 text-blue-700 animate-pulse" },
  ready:                    { label: "Ready",            class: "bg-emerald-100 text-emerald-700" },
  needs_review:             { label: "Needs review",     class: "bg-amber-100 text-amber-800" },
  customer_match_needed:    { label: "Customer match",   class: "bg-purple-100 text-purple-700" },
  duplicate_suspected:      { label: "Possible duplicate", class: "bg-rose-100 text-rose-700" },
  extraction_failed:        { label: "Extraction failed", class: "bg-rose-100 text-rose-700" },
  imported:                 { label: "Imported",         class: "bg-emerald-200 text-emerald-900" },
  skipped:                  { label: "Skipped",          class: "bg-zinc-100 text-zinc-500" },
};

const money = (n) => (Number(n)||0).toLocaleString("en-US",{style:"currency",currency:"USD"});
const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : "—";

export default function HistoricalImportBatch() {
  const { id } = useParams();
  const nav = useNavigate();
  const [batch, setBatch] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reviewDoc, setReviewDoc] = useState(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [confirmRollback, setConfirmRollback] = useState(false);
  const [rolling, setRolling] = useState(false);
  const dropRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/historical-imports/batches/${id}`);
      setBatch(data.batch); setDocs(data.documents || []);
    } catch { toast.error("Failed to load batch"); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Auto-poll while extracting
  useEffect(() => {
    if (docs.some((d) => d.status === "extracting")) {
      const t = setTimeout(load, 3000);
      return () => clearTimeout(t);
    }
  }, [docs, load]);

  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(
        `/historical-imports/batches/${id}/files`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${data.created?.length || 0} file(s) uploaded${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ""}`);
      if (data.skipped?.length) data.skipped.forEach((s) => toast.warning(`${s.filename}: ${s.reason}`));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.dataTransfer.files) onFiles(e.dataTransfer.files);
  };

  const extract = async () => {
    setExtracting(true);
    try {
      const { data } = await api.post(`/historical-imports/batches/${id}/extract`);
      toast.success(`Extraction started for ${data.queued} document(s)`);
      setTimeout(load, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setExtracting(false); }
  };

  const importReady = async () => {
    setConfirmImport(false); setImporting(true);
    try {
      const { data } = await api.post(`/historical-imports/batches/${id}/import`);
      toast.success(`Imported ${data.created_invoices} invoices · ${data.created_customers} customers`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const rollback = async () => {
    setConfirmRollback(false); setRolling(true);
    try {
      const { data } = await api.post(`/historical-imports/batches/${id}/rollback`);
      toast.success(`Rolled back: ${data.invoices_deleted} invoices, ${data.customers_deleted} customers`);
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : det?.reason || "Rollback failed");
    } finally { setRolling(false); }
  };

  const summary = useMemo(() => {
    const counts = {};
    docs.forEach((d) => { counts[d.status] = (counts[d.status]||0)+1; });
    return counts;
  }, [docs]);

  if (loading || !batch) return <div className="p-8 text-zinc-500">Loading…</div>;
  const readyCount = summary.ready || 0;
  const isImported = batch.status === "imported";
  const isRolledBack = batch.status === "rolled_back";

  return (
    <div className="p-8 max-w-7xl" data-testid="hist-batch-page">
      <button onClick={()=>nav("/app/imports/historical")} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4"/> All batches
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Import batch</div>
          <h1 className="font-heading text-3xl font-bold">{batch.label}</h1>
          <div className="text-sm text-zinc-500 mt-1">
            {batch.source_crm ? <>Source: <span className="font-medium">{batch.source_crm}</span> · </> : null}
            Status: <span className="font-medium">{batch.status}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isImported && !isRolledBack && (
            <>
              <Button variant="outline" onClick={()=>fileInputRef.current?.click()} disabled={uploading} data-testid="hist-upload-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Uploading…</> : <><Upload className="w-4 h-4 mr-1"/> Upload files</>}
              </Button>
              <input ref={fileInputRef} type="file" multiple hidden
                     accept=".pdf,.jpg,.jpeg,.png,.webp,.zip"
                     onChange={(e)=>onFiles(e.target.files)}
                     data-testid="hist-file-input"/>
              <Button variant="outline" onClick={extract} disabled={extracting || docs.length===0} data-testid="hist-extract-btn">
                {extracting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Extracting…</> : <><Sparkles className="w-4 h-4 mr-1"/> Run AI extraction</>}
              </Button>
              <Button
                onClick={()=>setConfirmImport(true)}
                disabled={importing || readyCount===0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="hist-import-btn"
              >
                {importing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Importing…</> : <>Import ready ({readyCount})</>}
              </Button>
            </>
          )}
          {isImported && (
            <Button variant="outline" onClick={()=>setConfirmRollback(true)} disabled={rolling}
                    className="text-rose-700 hover:bg-rose-50" data-testid="hist-rollback-btn">
              {rolling ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/> Rolling back…</> : <><RotateCcw className="w-4 h-4 mr-1"/> Rollback batch</>}
            </Button>
          )}
        </div>
      </div>

      {/* Metric strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6 text-sm">
        <Metric label="Uploaded" value={docs.length} />
        <Metric label="Ready" value={summary.ready || 0} tone="emerald" />
        <Metric label="Needs review" value={summary.needs_review || 0} tone="amber" />
        <Metric label="Duplicates" value={summary.duplicate_suspected || 0} tone="rose" />
        <Metric label="Failed" value={summary.extraction_failed || 0} tone="rose" />
        <Metric label="Imported" value={summary.imported || 0} tone="emerald" />
      </div>

      {/* Drop zone */}
      {!isImported && !isRolledBack && (
        <div
          ref={dropRef}
          onDragOver={(e)=>{e.preventDefault();}}
          onDrop={handleDrop}
          className="border-2 border-dashed border-zinc-300 rounded-xl p-6 text-center text-zinc-500 mb-6 hover:border-[#FF5A00] transition-colors"
          data-testid="hist-dropzone"
        >
          <Upload className="w-8 h-8 mx-auto mb-2 text-zinc-400"/>
          Drag &amp; drop PDFs, JPGs, PNGs, or ZIPs here — or click <strong>Upload files</strong>.
        </div>
      )}

      {/* Documents table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-zinc-50 border-b border-zinc-100 text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">
          <div className="col-span-3">File / Invoice #</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-3">Customer</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1 text-right">Paid</div>
          <div className="col-span-2">Status</div>
        </div>
        {docs.length === 0 ? (
          <div className="p-8 text-center text-zinc-400">Upload files to begin.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {docs.map((d) => {
              const ex = d.extracted || {};
              const sm = STATUS_META[d.status] || STATUS_META.uploaded;
              return (
                <button
                  key={d.id}
                  onClick={()=>setReviewDoc(d)}
                  className="w-full grid grid-cols-12 gap-3 px-4 py-3 items-center text-left hover:bg-zinc-50"
                  data-testid={`hist-doc-row-${d.id}`}
                >
                  <div className="col-span-3 truncate">
                    <div className="text-sm font-medium truncate">{d.original_filename}</div>
                    <div className="text-[11px] text-zinc-500 font-mono-accent truncate">
                      #{ex.invoice_number || "—"}
                    </div>
                  </div>
                  <div className="col-span-2 text-sm text-zinc-700">{fmtDate(ex.invoice_date)}</div>
                  <div className="col-span-3 text-sm text-zinc-700 truncate">
                    {(ex.customer?.name || "—")}
                    <div className="text-[11px] text-zinc-500 truncate">{ex.customer?.email || ex.customer?.phone || ""}</div>
                  </div>
                  <div className="col-span-1 text-right text-sm font-mono-accent">{money(ex.total)}</div>
                  <div className="col-span-1 text-right text-sm font-mono-accent">{money(ex.amount_paid)}</div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center text-[10px] uppercase font-mono-accent px-2 py-0.5 rounded ${sm.class}`} data-testid={`hist-doc-status-${d.id}`}>
                      {sm.label}
                    </span>
                    {d.warnings?.length > 0 && (
                      <div className="text-[10px] text-amber-700 mt-0.5 truncate flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3"/> {d.warnings[0]}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Review dialog */}
      <ReviewDialog doc={reviewDoc} batchId={id} onClose={()=>setReviewDoc(null)} onSaved={load} />

      {/* Import confirm */}
      <AlertDialog open={confirmImport} onOpenChange={setConfirmImport}>
        <AlertDialogContent data-testid="hist-import-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Import {readyCount} ready invoice(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates native LeadForge invoices dated on their original invoice dates.
              Customers, emails, SMS, and automations remain <strong>silent</strong>. This can
              be rolled back afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={importReady} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="hist-import-confirm-btn">
              Yes, import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rollback confirm */}
      <AlertDialog open={confirmRollback} onOpenChange={setConfirmRollback}>
        <AlertDialogContent data-testid="hist-rollback-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this import batch?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes every invoice AND customer created by this batch. Customers you
              matched to existing records are preserved. Invoices that have been paid via
              Stripe cannot be rolled back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={rollback} className="bg-rose-600 hover:bg-rose-700 text-white"
                                data-testid="hist-rollback-confirm-btn">
              Yes, roll back
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Metric({ label, value, tone = "zinc" }) {
  const colors = {
    zinc: "text-zinc-900", emerald: "text-emerald-700",
    amber: "text-amber-700", rose: "text-rose-700",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-3">
      <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
      <div className={`text-2xl font-bold font-mono-accent ${colors[tone]}`}>{value}</div>
    </div>
  );
}

function ReviewDialog({ doc, batchId, onClose, onSaved }) {
  const [ex, setEx] = useState(null);
  const [selectedCust, setSelectedCust] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (doc) {
      setEx(JSON.parse(JSON.stringify(doc.extracted || {})));
      setSelectedCust(doc.customer_match?.selected_customer_id || "");
    } else { setEx(null); }
  }, [doc?.id]);

  const setF = (path, v) => {
    setEx((cur) => {
      const c = { ...cur };
      const parts = path.split(".");
      let ref = c;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!ref[parts[i]]) ref[parts[i]] = {};
        ref = ref[parts[i]];
      }
      ref[parts[parts.length - 1]] = v;
      return c;
    });
  };

  const save = async ({ close = false } = {}) => {
    setBusy(true);
    try {
      await api.patch(`/historical-imports/batches/${batchId}/documents/${doc.id}`, {
        extracted: ex,
        selected_customer_id: selectedCust || null,
      });
      toast.success("Saved");
      onSaved?.();
      if (close) onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const forceImport = async () => {
    setBusy(true);
    try {
      await api.patch(`/historical-imports/batches/${batchId}/documents/${doc.id}`, {
        extracted: ex, selected_customer_id: selectedCust || null,
        force_import_despite_duplicate: true,
      });
      toast.success("Marked as ready — duplicate warning overridden");
      onSaved?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const skip = async () => {
    setBusy(true);
    try {
      await api.patch(`/historical-imports/batches/${batchId}/documents/${doc.id}`, { status: "skipped" });
      toast.success("Skipped");
      onSaved?.(); onClose?.();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!doc || !ex) return null;
  const dup = doc.duplicate_of;
  const cm = doc.customer_match || {};
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const fileUrl = `${backendUrl}/api/historical-imports/batches/${batchId}/documents/${doc.id}/file`;

  return (
    <Dialog open={!!doc} onOpenChange={(v)=>!v && onClose?.()}>
      <DialogContent className="max-w-6xl" data-testid="hist-review-dialog">
        <DialogHeader>
          <DialogTitle>Review invoice · {doc.original_filename}</DialogTitle>
          <DialogDescription>
            Edit any extracted value on the right. Nothing is imported until you confirm.
          </DialogDescription>
        </DialogHeader>

        {dup && (
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm mb-2" data-testid="hist-dup-warning">
            <AlertTriangle className="w-4 h-4 inline text-rose-600 mr-1"/>
            Possible duplicate — {dup.reason}. Existing invoice #{dup.invoice_number || dup.original_invoice_number}.
          </div>
        )}
        {doc.warnings?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs">
            <div className="uppercase font-mono-accent text-[10px] text-amber-700 mb-1">Reconciliation warnings</div>
            <ul className="list-disc list-inside space-y-0.5">
              {doc.warnings.map((w, i) => <li key={i} className="font-mono text-amber-900">{w}</li>)}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-zinc-200 rounded overflow-hidden h-[520px]">
            <div className="p-2 bg-zinc-50 border-b flex items-center justify-between text-xs">
              <span>Original source</span>
              <a href={fileUrl} target="_blank" rel="noreferrer" className="text-[#FF5A00] hover:underline flex items-center gap-1" data-testid="hist-view-original">
                <ExternalLink className="w-3 h-3"/> Open
              </a>
            </div>
            <iframe title="source" src={fileUrl} className="w-full h-[480px]" data-testid="hist-source-iframe"/>
          </div>

          <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-2">
              <FieldInput label="Invoice #" value={ex.invoice_number} onChange={(v)=>setF("invoice_number", v)} testid="hist-review-invoice-number"/>
              <FieldInput label="Date" type="date" value={(ex.invoice_date||"").slice(0,10)} onChange={(v)=>setF("invoice_date", v)} testid="hist-review-date"/>
              <FieldInput label="Due" type="date" value={(ex.due_date||"").slice(0,10)} onChange={(v)=>setF("due_date", v)}/>
              <FieldInput label="Paid date" type="date" value={(ex.paid_date||"").slice(0,10)} onChange={(v)=>setF("paid_date", v)}/>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono-accent">Customer</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input placeholder="Name" value={ex.customer?.name || ""} onChange={(e)=>setF("customer.name", e.target.value)} data-testid="hist-review-cust-name"/>
                <Input placeholder="Email" value={ex.customer?.email || ""} onChange={(e)=>setF("customer.email", e.target.value)}/>
                <Input placeholder="Phone" value={ex.customer?.phone || ""} onChange={(e)=>setF("customer.phone", e.target.value)}/>
                <Input placeholder="Service address" value={ex.customer?.service_address || ""} onChange={(e)=>setF("customer.service_address", e.target.value)}/>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase font-mono-accent">Match customer</Label>
              <Select value={selectedCust || "__none__"} onValueChange={(v)=>setSelectedCust(v==="__none__"?"":v)}>
                <SelectTrigger data-testid="hist-review-cust-select"><SelectValue placeholder="Select…"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not selected —</SelectItem>
                  <SelectItem value="__new__">➕ Create new customer</SelectItem>
                  {(cm.candidates || []).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} · {c.email || c.phone || ""} · {(c.score*100).toFixed(0)}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] uppercase font-mono-accent">Line items ({(ex.line_items||[]).length})</Label>
              <div className="space-y-1 mt-1">
                {(ex.line_items || []).map((li, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 items-center text-xs">
                    <Input className="col-span-5 h-8" value={li.name||""} onChange={(e)=>{const arr=[...ex.line_items];arr[i]={...arr[i],name:e.target.value};setF("line_items",arr);}}/>
                    <Input className="col-span-2 h-8" type="number" value={li.quantity||0} onChange={(e)=>{const arr=[...ex.line_items];arr[i]={...arr[i],quantity:parseFloat(e.target.value)||0};setF("line_items",arr);}}/>
                    <Input className="col-span-2 h-8" type="number" value={li.rate||0} onChange={(e)=>{const arr=[...ex.line_items];arr[i]={...arr[i],rate:parseFloat(e.target.value)||0};setF("line_items",arr);}}/>
                    <Input className="col-span-3 h-8" type="number" value={li.line_total||0} onChange={(e)=>{const arr=[...ex.line_items];arr[i]={...arr[i],line_total:parseFloat(e.target.value)||0};setF("line_items",arr);}}/>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <FieldInput label="Subtotal" type="number" value={ex.subtotal||""} onChange={(v)=>setF("subtotal", parseFloat(v)||0)}/>
              <FieldInput label="Discount" type="number" value={ex.discount||""} onChange={(v)=>setF("discount", parseFloat(v)||0)}/>
              <FieldInput label="Tax" type="number" value={ex.tax||""} onChange={(v)=>setF("tax", parseFloat(v)||0)}/>
              <FieldInput label="Total" type="number" value={ex.total||""} onChange={(v)=>setF("total", parseFloat(v)||0)} testid="hist-review-total"/>
              <FieldInput label="Paid" type="number" value={ex.amount_paid||""} onChange={(v)=>setF("amount_paid", parseFloat(v)||0)}/>
              <FieldInput label="Balance" type="number" value={ex.balance||""} onChange={(v)=>setF("balance", parseFloat(v)||0)}/>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase font-mono-accent">Payment status</Label>
                <Select value={ex.payment_status || "unknown"} onValueChange={(v)=>setF("payment_status", v)}>
                  <SelectTrigger data-testid="hist-review-payment-status"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["paid","partial","unpaid","void","refunded","unknown"].map((s)=><SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={skip} disabled={busy} data-testid="hist-review-skip">Skip this invoice</Button>
          {dup && (
            <Button variant="outline" onClick={forceImport} disabled={busy} data-testid="hist-review-force">
              Import anyway
            </Button>
          )}
          <Button onClick={()=>save({close:false})} disabled={busy} variant="outline">
            <Save className="w-4 h-4 mr-1"/> Save
          </Button>
          <Button onClick={()=>save({close:true})} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="hist-review-save-close">
            Save &amp; close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ label, value, onChange, type="text", testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase font-mono-accent">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e)=>onChange(e.target.value)} className="h-9"
             data-testid={testid}/>
    </div>
  );
}
